import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  DestroyRef,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  BrnInputOtp,
  HlmButton,
  HlmInputOtp,
  HlmInputOtpGroup,
  HlmInputOtpSlot,
  HlmLabel,
  HlmSpinnerImports,
} from "@pagai-fe/ui";
import { AuthenticateStateEnum } from "../../enums/authenticate.enum";
import { AuthService } from "../../services/auth.service";

@Component({
  selector: "app-confirm-sign-up",
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    BrnInputOtp,
    HlmInputOtp,
    HlmInputOtpGroup,
    HlmInputOtpSlot,
    HlmSpinnerImports,
  ],
  host: {
    class: "block",
  },
  templateUrl: "./confirm-sign-up.component.html",
})
export class ConfirmSignUpComponent {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  @Output()
  protected readonly confirmSignUpEvent =
    new EventEmitter<AuthenticateStateEnum>();

  protected readonly form = new FormGroup({
    code: new FormControl<string>("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ],
    }),
  });

  protected readonly transformPaste = (pastedText: string): string =>
    pastedText.replaceAll("-", "");

  protected confirmSignUp(): void {
    if (this.form.valid) {
      this.isLoading.set(true);
      const { code } = this.form.getRawValue();
      this._authService
        .confirmSignUp(code)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          error: () => this.isLoading.set(false),
          complete: () => this.isLoading.set(false),
        });
    }
  }

  protected onResendCodeClick(): void {
    this.isLoading.set(true);
    this._authService
      .resendCode()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (): void => this.isLoading.set(false),
        error: () => this.isLoading.set(false),
      });
  }

  protected onSignInClick(): void {
    this.confirmSignUpEvent.emit(AuthenticateStateEnum.SIGN_IN);
  }
}
